'use client';
import { getTemplateGenerateDesignUrl, urlParams } from '@components/Modules/TemplatesModule/utils/urls';
import { useClientConfig } from '@providers/ClientConfigProvider';
import Text, { TextVariant } from '@ui/Text/Text';
import cn from '@utils/cn';
import { DesignSizeTypeSetCategory, TemplateSetDesignsQuery } from 'graphql/generated/types.autogenerated';
import TemplateSetButton from './Buttons/TemplateSetButton';
import Star from './Star';
import TemplatesLayout from './TemplatesLayout';
import getButtonText from './TemplatesLayout/getButtonText';

type TemplatePreviewProps = {
  hash: string;
  design: TemplateSetDesignsQuery['templateSetDesigns'];
  isStaticFilterSelected?: boolean;
  layout?: DesignSizeTypeSetCategory;
  sizeCount?: number;
};

export default function TemplatePreview({
  hash,
  design,
  isStaticFilterSelected,
  layout,
  sizeCount,
}: TemplatePreviewProps) {
  const { APP_URL } = useClientConfig();
  const approximateCount = getApproximateCountNumber(design.setDesignCount);

  const designUrl =
    getTemplateGenerateDesignUrl(design.setHash ?? hash, APP_URL) +
    urlParams({
      isStatic: isStaticFilterSelected,
      category: layout,
      'skip-onboarding': true,
    });

  function renderApproximatePlus() {
    if ((design.setDesignCount ?? 0) > approximateCount) {
      return `Set containing ${approximateCount}+ banner sizes`;
    }
    return `Set containing ${approximateCount} banner sizes`;
  }

  function renderOverApproximate() {
    if (sizeCount) {
      return `${sizeCount} sizes`;
    }

    if ((design.setDesignCount ?? 0) > approximateCount) {
      return `Over ${approximateCount} sizes`;
    }

    return `${design.setDesignCount} sizes`;
  }

  return (
    <>
      <div
        className={cn(
          'order-2 md:order-1',
          'col-span-4 flex flex-col gap-8 self-center sm:col-span-6 md:col-span-4 lg:col-span-4',
        )}
      >
        <div className="flex flex-col gap-[18px]">
          <Text content="Create a set starting from this template" variant={TextVariant.TextJakartaMD} />
          <Text content={renderApproximatePlus()} variant={TextVariant.TextBodyMD} />
        </div>
        <div className="flex flex-col gap-2.5">
          <Star text="Fully adapted for a set" />
          <Star text={renderOverApproximate()} />
          <Star text="Add or remove sizes" />
          <Star text="Edit in bulk" />
          <Star text="Connect to data-feed" />
          <Star text="Add animations and transitions" />
        </div>
        <TemplateSetButton hash={hash} href={designUrl} text={getButtonText(layout, true)} />
      </div>
      <div className="order-1 col-span-4 self-center sm:col-span-6 md:order-2 md:col-span-4 lg:col-span-8">
        <TemplatesLayout layout={layout} design={design} />
      </div>
    </>
  );
}

function getApproximateCountNumber(count?: number | null) {
  if (getApproximateCount(count) > 0) {
    return getApproximateCount(count);
  }
  return count ?? 0;
}

function getApproximateCount(count?: number | null, approximateTo = 10) {
  return Math.floor((count ?? 0) / approximateTo) * approximateTo;
}
